// src/components/Hero.js

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import MessageSection from './ShortMessage';
import NotificationMarquee from './NotificationMarquee';
import StructuredData from './StructuredData';
import '../styles/Hero.scss'
import ContactUs from './ContactUs';
import Testimonials from './Testimonial';
import AboutSchool from './About';
import FacebookPagePosts from './FacebookPost';
import useScrollReveal from '../hooks/useScrollReveal';
import { BLOG_POSTS } from '../data/blogData';
import {
  GraduationCap, Users, Award, BookOpen,
  Star, Shield, Palette, Monitor, Heart,
  Trophy, CheckCircle, ArrowRight,
  ChevronDown, ChevronUp, MapPin, Phone,
  Navigation, Calendar, Clock
} from 'lucide-react';

// ── Data ────────────────────────────────────────────────────────────────────
const achievements = [
  '30 years of continuous educational excellence',
  'Affiliated to Bihar Government Board',
  'Smart exam evaluation & OMR testing powered by ScanScore (scanscore.app)',
  'Fully equipped Computer & Science Labs',
  'Sports facilities: Cricket, Volleyball, Athletics',
  'Cultural programmes: Dance, Art, Music',
  'Regular parent-teacher interaction programmes',
];

const legacyGallery = [
  {
    title: '30 years of trusted service',
    image: '/descriptive-image/thirty-years-service-award.png',
    alt: 'Thirty years service award at Prabhat Tara Vidya Niketan'
  },
  {
    title: 'Celebrating board toppers',
    image: '/descriptive-image/school-toppers-class-x-congratulations-2.png',
    alt: 'Board topper congratulations visual for Prabhat Tara Vidya Niketan'
  },
];

// ── SVG Wave Divider ─────────────────────────────────────────────────────────
const WaveDivider = ({ fill = '#ffffff', flip = false }) => (
  <div className={`wave-divider${flip ? ' wave-flip' : ''}`} aria-hidden="true">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 72" preserveAspectRatio="none">
      <path fill={fill} d="M0,36 C240,72 480,0 720,36 C960,72 1200,0 1440,36 L1440,72 L0,72 Z" />
    </svg>
  </div>
);

const PencilSVG = () => (
  <svg className="deco-svg deco-pencil" viewBox="0 0 60 60" fill="none" aria-hidden="true">
    <path d="M46 6l8 8L18 50l-8 2 2-8 34-38z" fill="currentColor" opacity=".12" />
    <path d="M46 6l8 8" stroke="currentColor" strokeWidth="2" opacity=".25" />
    <path d="M10 52l2-8" stroke="currentColor" strokeWidth="2" opacity=".2" />
  </svg>
);

const StarSVG = () => (
  <svg className="deco-svg deco-star" viewBox="0 0 50 50" fill="none" aria-hidden="true">
    <path d="M25 4l5.6 11.3L44 17.3l-9.5 9.3 2.2 13.1L25 33.5l-11.7 6.2 2.2-13.1L6 17.3l13.4-2 5.6-11.3z"
      fill="currentColor" opacity=".15" />
  </svg>
);

// ── Component ────────────────────────────────────────────────────────────────
export default function Hero() {
  const { t } = useTranslation();
  const revealRef = useScrollReveal();
  const [activeLandingFaq, setActiveLandingFaq] = useState(null);

  const toggleLandingFaq = (idx) => {
    setActiveLandingFaq(activeLandingFaq === idx ? null : idx);
  };

  const landingFaqs = [
    {
      q: "Which is the No. 1 private school in Goraul, Vaishali?",
      a: "Prabhat Tara Vidya Niketan (PTVN), located in Rajkhand, Goraul, is widely recognized as the No. 1 private school in Goraul and Vaishali district. Established in 1995, PTVN holds an unbroken 30-year legacy of 100% board exam pass rates, computer and science laboratories, phone-based ScanScore assessment, and an affordable, transparent fee structure from Nursery to Class 10."
    },
    {
      q: "Which is the No. 1 school near Goraul for Nursery to Class 10?",
      a: "Prabhat Tara Vidya Niketan is the No. 1 school near Goraul, serving students from Rajkhand, Sondho, Bhagwanpur, Mahua, Chehrakalan, and surrounding villages with dedicated, safe bus transport and comprehensive co-curricular programs."
    },
    {
      q: "Which is the best private school in Goraul, Vaishali?",
      a: "Prabhat Tara Vidya Niketan (PTVN), located in Rajkhand, Goraul, is recognized as the best private school in Goraul and Vaishali district. Established in 1995, PTVN delivers 30+ years of academic excellence, a 100% board examination pass rate, cutting-edge computer and science laboratories, smart phone-based ScanScore assessment, and an affordable, transparent fee structure from Nursery to Class 10."
    },
    {
      q: "Which is the best school near Goraul for Nursery to Class 10?",
      a: "Prabhat Tara Vidya Niketan is widely preferred by families looking for the best school near Goraul. Situated on a peaceful campus at Rajkhand, PTVN operates safe transport across Goraul, Bhagwanpur, Mahua, Sondho, and surrounding villages."
    },
    {
      q: "What is the fee structure at the best private school in Goraul Vaishali?",
      a: "PTVN offers an honest, pocket-friendly fee schedule with zero hidden charges or compulsory donation levies. Monthly tuition is tiered transparently for Pre-Primary, Primary, and High School levels, with flexible payment options and sibling concessions."
    },
    {
      q: "How does PTVN ensure 100% board examination success in Class 10?",
      a: "PTVN completes the Class 10 syllabus early by November, followed by three months of intensive revision, weekly OMR mock exams powered by ScanScore, doubt-clearing clinics, and individualized mentoring by experienced faculty."
    },
    {
      q: "How can parents apply for admission for academic session 2026-27?",
      a: "Parents can submit an online admission enquiry on our official portal (prabhattaravidyaniketan.in/admission-enquiry) or visit our admissions desk at Rajkhand, Goraul Monday through Saturday (8:00 AM – 3:30 PM). Direct telephone helpdesks are available at +91 76541 64726 and +91 99399 88606."
    }
  ];

  const stats = [
    { Icon: GraduationCap, value: '1200+', label: t('stats_students') },
    { Icon: Users,          value: '50+',   label: t('stats_educators') },
    { Icon: Award,          value: '30+',   label: t('stats_years') },
    { Icon: Trophy,         value: '100+',  label: t('stats_awards') },
  ];

  const features = [
    { Icon: BookOpen,  title: t('feature_academic_title'),   desc: t('feature_academic_desc') },
    { Icon: Star,      title: t('feature_holistic_title'),  desc: t('feature_holistic_desc') },
    { Icon: Shield,    title: t('feature_safe_title'),       desc: t('feature_safe_desc') },
    { Icon: Palette,   title: t('feature_creative_title'),      desc: t('feature_creative_desc') },
    { Icon: Monitor,   title: t('feature_digital_title'), desc: t('feature_digital_desc') },
    { Icon: Heart,     title: t('feature_faculty_title'),         desc: t('feature_faculty_desc') },
  ];

  const showcaseCards = [
    {
      tag: t('showcase_1_tag'),
      title: t('showcase_1_title'),
      desc: t('showcase_1_desc'),
      image: '/descriptive-image/students-with-parliament-models1.png',
      alt: 'Students standing with parliament models at school'
    },
    {
      tag: t('showcase_2_tag'),
      title: t('showcase_2_title'),
      desc: t('showcase_2_desc'),
      image: '/descriptive-image/students-holding-certificates-drawings-school.jpg',
      alt: 'Students holding certificates and drawings at school'
    },
  ];

  const programs = [
    { name: t('program_preprimary'), range: t('program_preprimary_desc'), tag: 'Pre-School',   color: '#ff6b2b' },
    { name: t('program_primary'),    range: t('program_primary_desc'),    tag: 'Foundation',   color: '#27ae60' },
    { name: t('program_middle'),     range: t('program_middle_desc'),     tag: 'Intermediate', color: '#0c278c' },
    { name: t('program_high'),       range: t('program_high_desc'),       tag: 'Bihar Board',  color: '#9b27af' },
  ];

  return (
    <div className='hero' ref={revealRef}>
      <StructuredData type="home" />
      <NotificationMarquee />

      {/* ── Hero Video Banner ── */}
      <section className='hero-banner'>
        <div className='hero-shell'>
          <div className='hero-content' data-reveal='left'>
            <span className='hero-badge'>
              <GraduationCap size={14} /> No. 1 Private School in Goraul · Admissions Open
            </span>
            <h1 className='hero-title'>
              {t('school_name')}
              <span className='hero-title-sub'>No. 1 Private School in Goraul, Vaishali</span>
            </h1>
            <p className='hero-subtitle'>
              Vaishali's No. 1 institution with 30+ years of academic excellence from Nursery to Class 10. Modern digital learning, caring faculty, and 100% board examination results.
            </p>
            <div className='hero-cta-group'>
              <a href='/admission-enquiry' className='cta-primary'>{t('apply_for_admission')}</a>
              <a href='/about-us' className='cta-secondary'>{t('explore_school')}</a>
            </div>
            <div className='hero-trust-row'>
              {['No. 1 School in Goraul', t('tag_bihar_board'), t('tag_years'), t('tag_digital_learning')].map(tag => (
                <span key={tag} className='trust-tag'>
                  <CheckCircle size={13} /> {tag}
                </span>
              ))}
            </div>
          </div>

          <div className='hero-media' data-reveal='right'>
            <div className='video-stage'>
              <div className='video-stage__meta'>
                <span className='video-stage__tag'>{t('welcome_film')}</span>
                <p>{t('welcome_film_desc')}</p>
              </div>
              <video
                src='/ptvn_hero.mp4'
                poster='/descriptive-image/school-building-front-view.jpg'
                autoPlay
                loop
                muted
                playsInline
                aria-label='Curtains opening to reveal the Prabhat Tara Vidya Niketan campus'
              />
            </div>
            <div className='video-caption'>
              <h2>{t('explore_campus_title')}</h2>
              <p>{t('explore_campus_desc')}</p>
            </div>
          </div>
        </div>

        <div className='hero-support-grid'>
          <div className='support-note' data-reveal>
            <span className='support-note__tag'>{t('why_parents_choose_title')}</span>
            <h3>{t('why_parents_choose_heading')}</h3>
            <ul className='support-note__list'>
              <li><CheckCircle size={16} /> {t('point_easy_admission')}</li>
              <li><CheckCircle size={16} /> {t('point_academics_holistic')}</li>
              <li><CheckCircle size={16} /> {t('point_parent_engagement')}</li>
            </ul>
          </div>

          <article className='support-card' data-reveal='right'>
            <div className='support-card__copy'>
              <span className='support-card__tag'>{t('our_campus_tag')}</span>
              <h3>{t('our_campus_heading')}</h3>
              <p>{t('our_campus_desc')}</p>
            </div>
            <img src='/descriptive-image/school-building-front-view.jpg' alt='Front view of the Prabhat Tara Vidya Niketan campus' loading='lazy' />
          </article>
        </div>

        <WaveDivider fill="#ffffff" />
      </section>

      {/* ── Stats ── */}
      <section className='stats-section'>
        <div className='stats-grid'>
          {stats.map(({ Icon, value, label }, i) => (
            <div key={i} className='stat-card' data-reveal data-reveal-delay={String(i + 1)}>
              <div className='stat-icon'><Icon size={34} strokeWidth={1.6} /></div>
              <span className='stat-value'>{value}</span>
              <span className='stat-label'>{label}</span>
            </div>
          ))}
        </div>
      </section>

      <WaveDivider fill="#f5f7ff" />

      {/* ── Director / Principal Messages ── */}
      <div className='section-bg-alt'>
        <MessageSection />
      </div>

      <WaveDivider fill="#ffffff" flip />

      {/* ── Why Choose Us ── */}
      <section className='why-us-section'>
        <PencilSVG />
        <StarSVG />
        <div className='section-header' data-reveal>
          <span className='section-tag'>{t('why_choose_tag')}</span>
          <h2>{t('why_choose_heading')}</h2>
          <p>{t('why_choose_subheading')}</p>
        </div>

        <div className='showcase-grid'>
          {showcaseCards.map(({ tag, title, desc, image, alt }, i) => (
            <article key={title} className='showcase-card' data-reveal data-reveal-delay={String(i + 1)}>
              <div className='showcase-card__media'>
                <img src={image} alt={alt} loading='lazy' />
              </div>
              <div className='showcase-card__body'>
                <span className='showcase-card__tag'>{tag}</span>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            </article>
          ))}
        </div>

        <div className='features-grid'>
          {features.map(({ Icon, title, desc }, i) => (
            <div key={i} className='feature-card' data-reveal data-reveal-delay={String(i + 1)}>
              <div className='feature-icon'><Icon size={26} strokeWidth={1.8} /></div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <WaveDivider fill="#f0f4ff" />

      {/* ── Programs Offered ── */}
      <section className='programs-section'>
        <div className='section-header light' data-reveal>
          <span className='section-tag accent'>{t('programs_tag')}</span>
          <h2>{t('programs_heading')}</h2>
          <p>{t('programs_subheading')}</p>
        </div>
        <div className='programs-grid'>
          {programs.map(({ name, range, tag, color }, i) => (
            <div key={i} className='program-card' style={{ '--prog-color': color }} data-reveal data-reveal-delay={String(i + 1)}>
              <span className='prog-tag'>{tag}</span>
              <h3>{name}</h3>
              <p className='prog-range'>{range}</p>
              <a href='/fee-structure' className='prog-link'>
                {t('fee_structure')} <ArrowRight size={14} />
              </a>
            </div>
          ))}
        </div>
        <WaveDivider fill="#ffffff" flip />
      </section>

      {/* ── Achievements List ── */}
      <section className='achievements-section'>
        <div className='achievements-inner'>
          <div className='achievements-text' data-reveal='left'>
            <span className='section-tag'>{t('legacy_tag')}</span>
            <h2>{t('legacy_heading')}</h2>
            <p>{t('legacy_subheading')}</p>
            <ul className='achievements-list'>
              {achievements.map((item, i) => (
                <li key={i}><CheckCircle size={16} /> {item}</li>
              ))}
            </ul>
            <a href='/about-us' className='cta-primary'>{t('explore_school')}</a>
          </div>
          <div className='achievements-visual' data-reveal='right'>
            {legacyGallery.map(({ title, image, alt }, index) => (
              <figure key={title} className={`achievement-photo achievement-photo--${index === 0 ? 'primary' : 'secondary'}`}>
                <img src={image} alt={alt} loading='lazy' />
                <figcaption>{title}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider fill="#f5f7ff" />

      {/* ── Facebook Posts ── */}
      <div className='section-bg-alt'>
        <FacebookPagePosts />
      </div>

      <WaveDivider fill="#ffffff" flip />

      {/* ── Play Class ── */}
      <section className='playSection'>
        <img src="/descriptive-image/school-welcome-cartoon-showing-dont-worry-school-is-other-home-4-panel-story.png" alt="Illustrated welcome story for younger learners at Prabhat Tara Vidya Niketan" loading="lazy" />
        <div>
          <span className='play-badge'>{t('play_school_heading')}</span>
          <h2>{t('play_section')}</h2>
          <p className='lab-desc'>For younger students and new parents, the first school experience matters. PTVN pairs structured learning with a warm welcome that makes every child feel settled.</p>
          <a href='/admission-enquiry' className='play-link'>Enquire Now <ArrowRight size={16} /></a>
        </div>
      </section>

      {/* ── Student Care ── */}
      <section className='firstSection'>
        <div className='firstSection__copy' data-reveal='left'>
          <span className='section-tag accent'>Student Care</span>
          <h2>School life here is not only about marks, but also about wellbeing and everyday care.</h2>
          <p>Health drives, attentive routines, and a visibly involved school culture help families trust the campus beyond academics alone.</p>
          <a href='/about-us' className='play-link'>See More About Campus Life <ArrowRight size={16} /></a>
        </div>
        <div className='firstSection__media' data-reveal='right'>
          <img src="/descriptive-image/nation-deworming-day-celebration.png" alt="Nation deworming day celebration at Prabhat Tara Vidya Niketan" loading="lazy" />
        </div>
      </section>

      {/* ── Computer Lab ── */}
      <section className='playSection lab-section'>
        <img src="/descriptive-image/students-learning-with-laptops-classroom.png" alt="Students learning with laptops in the classroom at Prabhat Tara Vidya Niketan" loading="lazy" />
        <div>
          <h2>{t('computer_lab_heading')}</h2>
          <p className='lab-desc'>Our modern computer lab equips students with essential digital skills for the 21st century.</p>
        </div>
      </section>

      <AboutSchool />
      <Testimonials />

      {/* ── SEO Authority Section: No. 1 Private School in Goraul, Vaishali ── */}
      <section className='seo-authority-section' data-reveal>
        <div className='seo-authority-container'>
          <div className='section-header'>
            <span className='section-tag accent'>Ranked No. 1 in Vaishali</span>
            <h2>Why Prabhat Tara Vidya Niketan is the No. 1 Private School in Goraul, Vaishali</h2>
            <p>
              Looking for the <strong>no. 1 private school in goraul</strong>, the <strong>no. 1 school in goraul vaishali</strong>, or the <strong>no. 1 school near goraul</strong>? 
              For over three decades, Prabhat Tara Vidya Niketan (PTVN) has delivered benchmark academic standards, character-building discipline, 
              and state-of-the-art digital infrastructure from Nursery to Class 10.
            </p>
          </div>

          <div className='seo-pillars-grid'>
            <div className='pillar-card'>
              <div className='pillar-icon'><Trophy size={28} /></div>
              <h3>30+ Years Proven Excellence</h3>
              <p>Founded in 1995, PTVN holds an unbroken record of 100% board examination pass rates, recognized as the <strong>no. 1 school in goraul</strong> by parents.</p>
            </div>

            <div className='pillar-card'>
              <div className='pillar-icon'><Monitor size={28} /></div>
              <h3>Smart Assessment & Modern Labs</h3>
              <p>Pioneering phone-based OMR testing powered by ScanScore (scanscore.app), paired with modern science practicals and computer education.</p>
            </div>

            <div className='pillar-card'>
              <div className='pillar-icon'><Navigation size={28} /></div>
              <h3>Safe Village Transport Network</h3>
              <p>Recognized as the premier <strong>no. 1 school near goraul vaishali</strong>, our supervised bus fleet connects Rajkhand, Sondho, Bhagwanpur, and Mahua.</p>
            </div>

            <div className='pillar-card'>
              <div className='pillar-icon'><Shield size={28} /></div>
              <h3>Transparent & Fair Fee Structure</h3>
              <p>The <strong>no. 1 private school in goraul vaishali</strong> with zero hidden costs, no compulsory donations, and flexible fee options tailored for families.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Google Business Profile & Local Map Trust Block ── */}
      <section className='gbp-trust-block' data-reveal>
        <div className='gbp-shell'>
          <div className='gbp-info'>
            <div className='gbp-badge'>
              <Star size={16} fill="#f0a500" color="#f0a500" />
              <span>4.9 / 5.0 Rated on Google Business</span>
            </div>
            <h2>Visit the Top Rated School in Goraul & Vaishali</h2>
            <p>
              Prabhat Tara Vidya Niketan is conveniently situated in Rajkhand, Goraul, just minutes away from 
              Goraul Railway Station and the NH-22 corridor. We welcome parents to experience our green, distraction-free campus firsthand.
            </p>
            <div className='gbp-nap-details'>
              <div className='nap-item'>
                <MapPin size={18} color="#ff6b2b" />
                <span>Rajkhand, Block - Goraul, District - Vaishali, Bihar - 844114</span>
              </div>
              <div className='nap-item'>
                <Phone size={18} color="#ff6b2b" />
                <span>Admissions Helpline: +91 76541 64726 / +91 99399 88606</span>
              </div>
            </div>
            <div className='gbp-actions'>
              <a 
                href='https://www.google.com/maps/place/Prabhat+Tara+Vidya+Niketan+Goraul/@25.9272702,85.3309645,783m/data=!3m2!1e3!4b1!4m6!3m5!1s0x39ed6b88a06921db:0x62a848a0ca5eb38b!8m2!3d25.9272702!4d85.3309645!16s%2Fg%2F11fd6c0c96' 
                target='_blank' 
                rel='noopener noreferrer' 
                className='btn-gbp-maps'
              >
                <Navigation size={16} /> Get Directions on Google Maps
              </a>
              <a 
                href='https://www.google.com/maps/place/Prabhat+Tara+Vidya+Niketan+Goraul/@25.9272702,85.3309645,783m/data=!3m2!1e3!4b1!4m6!3m5!1s0x39ed6b88a06921db:0x62a848a0ca5eb38b!8m2!3d25.9272702!4d85.3309645!16s%2Fg%2F11fd6c0c96' 
                target='_blank' 
                rel='noopener noreferrer' 
                className='btn-gbp-reviews'
              >
                <Star size={16} /> Read Google Reviews
              </a>
            </div>
          </div>

          <div className='gbp-map-card'>
            <iframe
              title='Prabhat Tara Vidya Niketan Google Map Location'
              src='https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14352.96890139945!2d85.3309645!3d25.9272702!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39ed6b88a06921db%3A0x62a848a0ca5eb38b!2sPrabhat%20Tara%20Vidya%20Niketan%20Goraul!5e0!3m2!1sen!2sin!4v1733709839481!5m2!1sen!2sin'
              width='100%'
              height='300'
              style={{ border: 0, borderRadius: '16px' }}
              allowFullScreen=''
              loading='lazy'
              referrerPolicy='no-referrer-when-downgrade'
            />
          </div>
        </div>
      </section>

      {/* ── Frequently Asked Questions (Landing Page FAQ) ── */}
      <section className='landing-faq-section' data-reveal>
        <div className='faq-shell'>
          <div className='section-header'>
            <span className='section-tag'>Got Questions?</span>
            <h2>Frequently Asked Questions About Schools in Goraul</h2>
            <p>Everything you need to know about admissions, academics, and campus life at the best private school in Goraul.</p>
          </div>

          <div className='faq-accordion-wrap'>
            {landingFaqs.map((faq, idx) => (
              <div key={idx} className={`faq-card ${activeLandingFaq === idx ? 'expanded' : ''}`}>
                <button 
                  className='faq-trigger' 
                  onClick={() => toggleLandingFaq(idx)}
                  aria-expanded={activeLandingFaq === idx}
                >
                  <span className='faq-q-text'>{faq.q}</span>
                  {activeLandingFaq === idx ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                {activeLandingFaq === idx && (
                  <div className='faq-ans-body'>
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Latest Educational Guides & Blogs Showcase ── */}
      <section className='landing-blog-section' data-reveal>
        <div className='landing-blog-shell'>
          <div className='section-header'>
            <span className='section-tag accent'>Knowledge Hub</span>
            <h2>Educational Guides for Parents in Goraul & Vaishali</h2>
            <p>Expert articles on board exam preparation, nursery admissions, school fee structures, and campus infrastructure.</p>
          </div>

          <div className='landing-blog-grid'>
            {BLOG_POSTS.slice(0, 3).map(post => (
              <article key={post.id} className='landing-post-card'>
                <div className='landing-post-img'>
                  <img src={post.coverImage} alt={post.title} loading='lazy' />
                  <span className='post-cat'>{post.category}</span>
                </div>
                <div className='landing-post-body'>
                  <div className='post-meta-line'>
                    <span><Calendar size={12} /> {post.publishDate}</span>
                    <span>•</span>
                    <span><Clock size={12} /> {post.readTime}</span>
                  </div>
                  <h3>
                    <a href={`/blog/${post.slug}`}>
                      {post.title}
                    </a>
                  </h3>
                  <p>{post.excerpt.slice(0, 115)}...</p>
                  <a href={`/blog/${post.slug}`} className='post-read-more'>
                    <span>Read Article</span>
                    <ArrowRight size={14} />
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className='view-all-blogs-cta'>
            <a href='/blogs' className='cta-all-blogs'>
              <span>Explore All 10 School Guides</span>
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <ContactUs />
    </div>
  );
}
